import type {
  NoticeBlock,
  NoticeCatalog,
  NoticeColumn,
  NoticeComposition,
  NoticeContextField,
  NoticeContextList,
  NoticeStyle,
} from './notice-fidelity.models';

export type NoticeStructureCommand =
  | {
      type: 'insert';
      kind: NoticeBlock['kind'];
      anchorId: string;
      position: 'before' | 'after';
    }
  | { type: 'duplicate'; blockId: string }
  | { type: 'remove'; blockId: string }
  | { type: 'move'; blockId: string; direction: -1 | 1 }
  | { type: 'list'; blockId: string; source: string }
  | { type: 'column-add'; blockId: string }
  | { type: 'column-remove'; blockId: string; columnId: string }
  | {
      type: 'column-move';
      blockId: string;
      columnId: string;
      direction: -1 | 1;
    }
  | {
      type: 'column-edit';
      blockId: string;
      columnId: string;
      property: 'label' | 'field' | 'format';
      value: string;
    }
  | {
      type: 'column-edit';
      blockId: string;
      columnId: string;
      property: 'width';
      value: number;
    };

export type NoticeStructureResult =
  | {
      ok: true;
      composition: NoticeComposition;
      selectedBlockId: string;
    }
  | { ok: false; message: string };

const identifier = /^[a-z][a-z0-9_-]{0,47}$/;
const text = /^[\t\n\r\x20-\x7e]*$/;
const registeredContract = 'notice-document-context-proof';
const fail = (message: string): NoticeStructureResult => ({
  ok: false,
  message,
});

/** Atomic structure edits of the retained source, never a second editor model. */
export function applyNoticeStructureCommand(
  document: NoticeComposition,
  catalog: NoticeCatalog,
  command: NoticeStructureCommand,
): NoticeStructureResult {
  const next = structuredClone(document);
  const targetId =
    command.type === 'insert' ? command.anchorId : command.blockId;
  const index = next.blocks.findIndex((block) => block.id === targetId);
  if (index < 0)
    return fail('Select an existing block before changing its structure.');
  const block = next.blocks[index];
  let selectedBlockId = block.id;

  switch (command.type) {
    case 'insert': {
      if (command.position !== 'before' && command.position !== 'after')
        return fail('Choose insertion before or after the selected block.');
      const created = newBlock(next, catalog, command.kind);
      if (!created)
        return fail('A table needs a registered list with supported fields.');
      next.blocks.splice(
        index + (command.position === 'after' ? 1 : 0),
        0,
        created,
      );
      selectedBlockId = created.id;
      break;
    }
    case 'duplicate': {
      const copy = structuredClone(block);
      copy.id = uniqueId(next.blocks, 'block');
      next.blocks.splice(index + 1, 0, copy);
      selectedBlockId = copy.id;
      break;
    }
    case 'remove':
      if (next.blocks.length === 1)
        return fail('Keep at least one content block.');
      next.blocks.splice(index, 1);
      selectedBlockId = next.blocks[Math.min(index, next.blocks.length - 1)].id;
      break;
    case 'move': {
      const target = index + command.direction;
      if (
        !direction(command.direction) ||
        target < 0 ||
        target >= next.blocks.length
      )
        return fail('This block cannot move further in that direction.');
      [next.blocks[index], next.blocks[target]] = [next.blocks[target], block];
      break;
    }
    default: {
      if (block.kind !== 'repeat' || !block.columns)
        return fail('Select a table before changing its columns or list.');
      const result = editTable(next, catalog, block, command);
      if (result) return fail(result);
    }
  }

  const issue = structuralIssue(next, catalog);
  return issue ? fail(issue) : { ok: true, composition: next, selectedBlockId };
}

function editTable(
  document: NoticeComposition,
  catalog: NoticeCatalog,
  block: NoticeBlock,
  command: Exclude<
    NoticeStructureCommand,
    { type: 'insert' | 'duplicate' | 'remove' | 'move' }
  >,
): string | undefined {
  const columns = block.columns!;
  const registered = document.contractCode === registeredContract;
  if (command.type === 'list') {
    if (!registered) {
      if (command.source !== 'notice.items')
        return 'Choose the retained notice.items list.';
      block.source = command.source;
      return undefined;
    }
    const list = lists(catalog).find((item) => item.code === command.source);
    if (!list?.fields.length)
      return 'Choose a registered list with supported fields.';
    block.source = list.code;
    for (const column of columns) {
      const retained = list.fields.find((field) => field.code === column.field);
      const field = retained ?? list.fields[0];
      column.field = field.code;
      if (
        !retained ||
        (column.format !== undefined && !field.formats.includes(column.format))
      )
        column.format = field.formats[0];
    }
    return undefined;
  }
  if (command.type === 'column-add') {
    if (columns.length >= 4) return 'A table supports at most four columns.';
    const list = registered
      ? lists(catalog).find((item) => item.code === block.source)
      : undefined;
    if (registered && !list)
      return 'Choose a registered table list before adding a column.';
    if (!registered && block.source !== 'notice.items')
      return 'Choose the retained notice.items list before adding a column.';
    const fields = list?.fields ?? [];
    const field =
      fields.find(
        (item) => !columns.some((column) => column.field === item.code),
      ) ?? fields[0];
    if (registered && !field)
      return 'The selected list has no supported fields.';
    columns.push(
      field
        ? columnFor(field, uniqueId(columns, 'column'), 1)
        : {
            id: uniqueId(columns, 'column'),
            label: 'Value',
            field: 'value',
            width: 1,
          },
    );
    return undefined;
  }
  const index = columns.findIndex((column) => column.id === command.columnId);
  if (index < 0) return 'Select an existing table column.';
  const column = columns[index];
  switch (command.type) {
    case 'column-remove':
      if (columns.length <= 2) return 'Keep at least two table columns.';
      columns.splice(index, 1);
      return undefined;
    case 'column-move': {
      const target = index + command.direction;
      if (
        !direction(command.direction) ||
        target < 0 ||
        target >= columns.length
      )
        return 'This column cannot move further in that direction.';
      [columns[index], columns[target]] = [columns[target], column];
      return undefined;
    }
    case 'column-edit':
      switch (command.property) {
        case 'label':
          if (
            typeof command.value !== 'string' ||
            command.value.length > 80 ||
            !text.test(command.value)
          )
            return 'Use a column heading of at most 80 printable ASCII characters or line/tab whitespace.';
          column.label = command.value;
          return undefined;
        case 'width':
          if (
            !Number.isFinite(command.value) ||
            command.value < 1 ||
            command.value > 4
          )
            return 'Choose a width from 1 through 4.';
          column.width = command.value;
          return undefined;
        case 'field': {
          if (!registered) {
            if (command.value !== 'label' && command.value !== 'value')
              return 'Choose the label or value row field.';
            column.field = command.value;
            delete column.format;
            return undefined;
          }
          const field = lists(catalog)
            .find((item) => item.code === block.source)
            ?.fields.find((item) => item.code === command.value);
          if (!field)
            return 'Choose a registered field from the selected table list.';
          column.field = field.code;
          column.format = field.formats[0];
          return undefined;
        }
        case 'format': {
          const field = registered
            ? lists(catalog)
                .find((item) => item.code === block.source)
                ?.fields.find((item) => item.code === column.field)
            : undefined;
          if (!field)
            return 'Choose a permitted format for this registered row field.';
          if (command.value === '') {
            delete column.format;
            return undefined;
          }
          if (!field.formats.includes(command.value))
            return 'Choose a permitted format for this registered row field.';
          column.format = command.value;
          return undefined;
        }
      }
  }
}

function newBlock(
  document: NoticeComposition,
  catalog: NoticeCatalog,
  kind: NoticeBlock['kind'],
): NoticeBlock | undefined {
  const id = uniqueId(document.blocks, 'block');
  if (kind === 'pageBreak') return { id, kind };
  if (kind !== 'paragraph' && kind !== 'box' && kind !== 'repeat')
    return undefined;
  const style: NoticeStyle = {
    fontSizePt: document.page.fontSizePt,
    color: '#172B3A',
    backgroundColor: '#FFFFFF',
    borderColor: '#183B56',
    borderWidthPt: kind === 'paragraph' ? 0 : 1,
    paddingPt: kind === 'paragraph' ? 0 : 6,
    keepTogether: false,
  };
  if (kind !== 'repeat')
    return {
      id,
      kind,
      style,
      runs: [
        {
          kind: 'literal',
          value: 'Editable notice text',
          bold: false,
          italic: false,
        },
      ],
    };
  if (document.contractCode !== registeredContract)
    return {
      id,
      kind,
      style,
      source: 'notice.items',
      columns: [
        { id: 'label', label: 'Item', field: 'label', width: 1 },
        { id: 'value', label: 'Value', field: 'value', width: 3 },
      ],
    };
  const list = lists(catalog).find((item) => item.fields.length > 0);
  if (!list) return undefined;
  return {
    id,
    kind,
    style,
    source: list.code,
    columns: [list.fields[0], list.fields[1] ?? list.fields[0]].map(
      (field, index) =>
        columnFor(field, `column-${index + 1}`, index === 0 ? 1 : 3),
    ),
  };
}

function columnFor(
  field: NoticeContextField,
  id: string,
  width: number,
): NoticeColumn {
  return {
    id,
    label: field.label,
    field: field.code,
    width,
    format: field.formats[0],
  };
}
function lists(catalog: NoticeCatalog): NoticeContextList[] {
  return (catalog.documentContext?.lists ?? []).map((list) => ({
    ...list,
    fields: noticeStructureListFields(list),
  }));
}

/** Registered row choices shared by factories and the table inspector. */
export function noticeStructureListFields(
  list: NoticeContextList,
): NoticeContextField[] {
  const codes = new Set<string>();
  return list.fields.flatMap((field) => {
    const formats = field.formats.filter(
      (format) => typeof format === 'string' && !!format.trim(),
    );
    if (!field.code.trim() || codes.has(field.code) || !formats.length)
      return [];
    codes.add(field.code);
    return [{ ...field, formats }];
  });
}
function uniqueId(items: readonly { id: string }[], prefix: string): string {
  let suffix = 1;
  while (items.some((item) => item.id === `${prefix}-${suffix}`)) suffix++;
  return `${prefix}-${suffix}`;
}
function direction(value: number): value is -1 | 1 {
  return value === -1 || value === 1;
}
function structuralIssue(
  document: NoticeComposition,
  catalog: NoticeCatalog,
): string | undefined {
  const limit = Math.min(16, positiveLimit(catalog.limits.blocks, 16));
  if (document.blocks.length < 1 || document.blocks.length > limit)
    return `Use one to ${limit} blocks.`;
  const blockIds = new Set<string>();
  for (let index = 0; index < document.blocks.length; index++) {
    const block = document.blocks[index];
    if (!identifier.test(block.id) || blockIds.has(block.id))
      return 'Block identities must be valid and unique.';
    blockIds.add(block.id);
    if (
      block.kind === 'pageBreak' &&
      (index === 0 ||
        index === document.blocks.length - 1 ||
        document.blocks[index - 1].kind === 'pageBreak')
    )
      return 'A page break must separate content blocks; move or remove it first.';
    if (block.kind !== 'repeat') continue;
    if (!block.columns || block.columns.length < 2 || block.columns.length > 4)
      return 'A table needs two to four columns.';
    const columnIds = new Set<string>();
    for (const column of block.columns) {
      if (!identifier.test(column.id) || columnIds.has(column.id))
        return 'Column identities must be valid and unique within their table.';
      columnIds.add(column.id);
    }
  }
  const bytes = new TextEncoder().encode(
    JSON.stringify(document, null, 2),
  ).byteLength;
  if (bytes > Math.min(32768, positiveLimit(catalog.limits.sourceBytes, 32768)))
    return 'This edit exceeds the supported source byte limit; shorten or remove content first.';
  return undefined;
}
function positiveLimit(value: number, fallback: number): number {
  return Number.isInteger(value) && value > 0 ? value : fallback;
}
